import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { MailWarning, RefreshCw, Loader2, Clock } from 'lucide-react';

const fmtTs = (v) => {
  if (!v) return 'Never';
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
};

export default function MissingBillsJobPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/super-admin/intake/missing-bills/job-status');
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/super-admin/intake/missing-bills/run-now');
      const s = data.summary || {};
      toast.success(`Flagger ran across ${s.orgs ?? 0} orgs · ${s.late_arriving ?? 0} late · ${s.missing ?? 0} missing`);
      await load();
    } catch {
      toast.error('Failed to run flagger');
    } finally {
      setRunning(false);
    }
  };

  const last = status?.last_run;
  const cur = status?.current || {};

  return (
    <Card className="p-5 mb-6" data-testid="cockpit-missing-bills-job">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <MailWarning className="h-5 w-5 text-primary" />
          </div>
          <div>
            <div className="font-semibold flex items-center gap-2">
              Missing Bills — Daily Flagger
              <Badge variant="secondary">Daily</Badge>
            </div>
            <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
              <Clock className="h-3.5 w-3.5" />
              Last run: {loading ? '…' : fmtTs(last?.ts)}
              {last ? ` · ${last.orgs ?? 0} orgs · ${last.late_arriving ?? 0} late · ${last.missing ?? 0} missing · ${last.archived_received ?? 0} received` : ''}
            </div>
            <div className="text-sm mt-1">
              Currently flagged:{' '}
              <span className="text-amber-600 font-medium">{cur.late_arriving ?? 0} late</span>{' · '}
              <span className="text-red-600 font-medium">{cur.missing ?? 0} missing</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="cockpit-mb-refresh">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={runNow} disabled={running} data-testid="cockpit-mb-run-now">
            {running ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Run now
          </Button>
        </div>
      </div>
    </Card>
  );
}
